import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthState } from '@okta/okta-auth-js';
import { oktaAuth } from '../config/auth.config';
import { validateUserGroups } from '../config/authorization.config';
import { isLocalEnvironment } from '../config/environment.config';
import { trackLogin } from '../services/analyticsService';

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any | null;
  userGroups: string[];
  matchedGroups: string[];
  isAuthorized: boolean;
  authorizationError: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshTokens: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<any>(null);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [matchedGroups, setMatchedGroups] = useState<string[]>([]);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [authorizationError, setAuthorizationError] = useState<string | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        
        // Check if we're in local environment and have local auth bypass
        if (isLocalEnvironment()) {
          const localAuthBypass = sessionStorage.getItem('localAuthBypass');
          const localUserDataStr = sessionStorage.getItem('localUserData');
          
          if (localAuthBypass === 'true' && localUserDataStr) {
            console.log('🏠 Local environment - restoring local authentication');
            
            try {
              const mockUser = JSON.parse(localUserDataStr);
              const groups = mockUser.groups || [];
              
              // For local environment, bypass all authorization checks on restore too
              // Restore authentication state for local environment - SKIP GROUP VALIDATION
              setUser(mockUser);
              setUserGroups(groups);
              setIsAuthorized(true); // Always authorized in local
              setAuthorizationError(null); // No authorization errors in local
              setMatchedGroups(groups); // All groups are "matched" in local
              setIsAuthenticated(true);
              setIsLoading(false);
              
              console.log('✅ Local authentication restored successfully');
              return;
            } catch (parseError) {
              console.warn('⚠️ Failed to parse local user data, clearing local auth');
              sessionStorage.removeItem('localAuthBypass');
              sessionStorage.removeItem('localUserData');
            }
          }
          
          // If no local auth bypass or parsing failed, set unauthenticated state for local
          console.log('🏠 Local environment - no local authentication found');
          setIsAuthenticated(false);
          setUser(null);
          setUserGroups([]);
          setMatchedGroups([]);
          setIsAuthorized(false);
          setAuthorizationError(null);
          setIsLoading(false);
          return;
        }
        
        // For non-local environments, proceed with Okta authentication
        console.log('🔐 Non-local environment - checking Okta authentication');
        
        // First, check if there's an active OKTA session even if local tokens are expired
        try {
          console.log('🔄 Checking for existing OKTA session...');
          await oktaAuth.start();
        } catch (startError) {
          console.log('ℹ️ No existing session found during start:', startError);
        }
        
        // Check if user is authenticated (including after potential token refresh)
        const authState = await oktaAuth.authStateManager.getAuthState();
        
        console.log('🔍 Auth state check:', {
          isAuthenticated: authState?.isAuthenticated,
          accessToken: authState?.accessToken ? 'present' : 'missing',
          idToken: authState?.idToken ? 'present' : 'missing',
        });
        
        if (authState?.isAuthenticated) {
          // Get user info and groups BEFORE setting isAuthenticated
          const userInfo = await oktaAuth.getUser();
          setUser(userInfo);
          
          // Debug: Log the complete user info to see what we're getting
          console.log('🔍 Complete userInfo object:', userInfo);
          console.log('🔍 userInfo.groups type:', typeof userInfo.groups);
          console.log('🔍 userInfo.groups value:', userInfo.groups);
          
          // Debug: Check ID token directly for groups
          try {
            const idToken = await oktaAuth.tokenManager.get('idToken');
            console.log('🎫 ID Token:', idToken);
            if (idToken && 'claims' in idToken) {
              console.log('🎫 ID Token claims:', (idToken as any).claims);
              console.log('🎫 ID Token groups claim:', (idToken as any).claims?.groups);
              console.log('🎫 ID Token SPGGroups claim:', (idToken as any).claims?.SPGGroups);
            }
          } catch (tokenError) {
            console.log('⚠️ Could not retrieve ID token:', tokenError);
          }
          
          // Extract groups from user info - check both standard and SPGlobal-specific properties
          let groups: string[] = [];
          
          // Check for standard groups property
          if (Array.isArray(userInfo.groups)) {
            groups = userInfo.groups as string[];
          }
          // Check for SPGlobal-specific groups property
          else if (Array.isArray(userInfo.SPGGroups)) {
            groups = userInfo.SPGGroups as string[];
            console.log('📍 Using SPGGroups property for group data');
          }
          // Fallback to empty array
          else {
            groups = [];
          }
          
          console.log('🔍 Processed groups array:', groups);
          
          // Validate authorization using centralized config
          const { isAuthorized: authorized, error, matchedGroups: matched } = validateUserGroups(groups);
          
          // Set all states together to prevent race conditions
          setUser(userInfo);
          setUserGroups(groups);
          setIsAuthorized(authorized);
          setAuthorizationError(error);
          setMatchedGroups(matched);
          setIsAuthenticated(true); // Set this AFTER authorization is determined
          
          if (!authorized) {
            console.warn('User authenticated but not authorized:', error);
          }
        } else {
          console.log('🔐 User not authenticated - checking for recoverable session...');
          
          // Try to recover session by checking if we have valid tokens that just need refresh
          try {
            const tokens = await oktaAuth.tokenManager.getTokens();
            if (tokens.accessToken || tokens.idToken) {
              console.log('🔄 Found existing tokens, attempting refresh...');
              await oktaAuth.tokenManager.renew('accessToken');
              
              // Re-check auth state after token refresh
              const refreshedAuthState = await oktaAuth.authStateManager.getAuthState();
              if (refreshedAuthState?.isAuthenticated) {
                console.log('✅ Successfully recovered session after token refresh');
                // Process the authenticated state
                const userInfo = await oktaAuth.getUser();
                
                // Extract groups logic (same as above)
                let groups: string[] = [];
                if (Array.isArray(userInfo.groups)) {
                  groups = userInfo.groups as string[];
                } else if (Array.isArray(userInfo.SPGGroups)) {
                  groups = userInfo.SPGGroups as string[];
                  console.log('📍 Using SPGGroups property for group data');
                } else {
                  groups = [];
                }
                
                const { isAuthorized: authorized, error, matchedGroups: matched } = validateUserGroups(groups);
                
                // Set all states together
                setUser(userInfo);
                setUserGroups(groups);
                setIsAuthorized(authorized);
                setAuthorizationError(error);
                setMatchedGroups(matched);
                setIsAuthenticated(true);
                
                if (!authorized) {
                  console.warn('User authenticated but not authorized:', error);
                }
              } else {
                // Still not authenticated after refresh attempt
                setIsAuthenticated(false);
                setUser(null);
                setUserGroups([]);
                setMatchedGroups([]);
                setIsAuthorized(false);
                setAuthorizationError(null);
              }
            } else {
              // No tokens found
              setIsAuthenticated(false);
              setUser(null);
              setUserGroups([]);
              setMatchedGroups([]);
              setIsAuthorized(false);
              setAuthorizationError(null);
            }
          } catch (tokenError) {
            console.log('⚠️ Token refresh failed:', tokenError);
            setIsAuthenticated(false);
            setUser(null);
            setUserGroups([]);
            setMatchedGroups([]);
            setIsAuthorized(false);
            setAuthorizationError(null);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setIsAuthenticated(false);
        setUser(null);
        setUserGroups([]);
        setMatchedGroups([]);
        setIsAuthorized(false);
        setAuthorizationError(null);
      } finally {
        setIsLoading(false);
      }
    };

    // Subscribe to auth state changes (only for non-local environments)
    const handleAuthStateChange = (authState: AuthState) => {
      // Skip Okta auth state changes if we're in local environment
      if (isLocalEnvironment()) {
        return;
      }
      
      if (authState.isAuthenticated) {
        // Set loading during user info fetch to prevent race conditions
        setIsLoading(true);
        // Fetch user info and validate groups when authenticated
        oktaAuth.getUser().then((userInfo) => {
          
          // Debug: Log the complete user info in auth state change too
          console.log('🔄 Auth state change - userInfo:', userInfo);
          console.log('🔄 Auth state change - groups:', userInfo.groups);
          
          // Extract and validate groups - check both standard and SPGlobal-specific properties
          let groups: string[] = [];
          
          if (Array.isArray(userInfo.groups)) {
            groups = userInfo.groups as string[];
          } else if (Array.isArray(userInfo.SPGGroups)) {
            groups = userInfo.SPGGroups as string[];
            console.log('🔄 Auth state change - Using SPGGroups property');
          } else {
            groups = [];
          }
          
          const { isAuthorized: authorized, error, matchedGroups: matched } = validateUserGroups(groups);
          
          // Set all states together to prevent race conditions
          setUser(userInfo);
          setUserGroups(groups);
          setIsAuthorized(authorized);
          setAuthorizationError(error);
          setMatchedGroups(matched);
          setIsAuthenticated(true); // Set this AFTER authorization is determined
          setIsLoading(false); // Only set loading to false after all auth processing is complete
        }).catch((error) => {
          console.error('Error fetching user info during auth state change:', error);
          setIsLoading(false);
        });
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setUserGroups([]);
        setMatchedGroups([]);
        setIsAuthorized(false);
        setAuthorizationError(null);
        setIsLoading(false);
      }
    };

    // Only subscribe to Okta auth state changes if not in local environment
    if (!isLocalEnvironment()) {
      oktaAuth.authStateManager.subscribe(handleAuthStateChange);
    }
    
    initAuth();

    // Cleanup subscription
    return () => {
      if (!isLocalEnvironment()) {
        oktaAuth.authStateManager.unsubscribe(handleAuthStateChange);
      }
    };
  }, []);

  const login = async (): Promise<void> => {
    try {
      // Check if we're in local environment
      if (isLocalEnvironment()) {
        console.log('🏠 Local environment detected - bypassing Okta authentication');
        
        // Create mock user data for local development
        const mockUser = {
          sub: 'local-dev-user',
          email: 'local.dev@spglobal.com',
          given_name: 'Local',
          family_name: 'Developer',
          name: 'Local Developer',
          preferred_username: 'local.dev@spglobal.com',
          groups: ['GRP_REV_LANDING_PAGE_DEV_USERS'],
          SPGGroups: ['GRP_REV_LANDING_PAGE_DEV_USERS']
        };
        
        // For local environment, bypass all authorization checks
        const groups = mockUser.groups;
        
        // Set authentication state directly for local environment - SKIP GROUP VALIDATION
        setUser(mockUser);
        setUserGroups(groups);
        setIsAuthorized(true); // Always authorized in local
        setAuthorizationError(null); // No authorization errors in local
        setMatchedGroups(groups); // All groups are "matched" in local
        setIsAuthenticated(true);
        setIsLoading(false);
        
        // Store local auth flag in sessionStorage to persist across page refreshes
        sessionStorage.setItem('localAuthBypass', 'true');
        sessionStorage.setItem('localUserData', JSON.stringify(mockUser));
        
        console.log('✅ Local authentication successful');
        trackLogin(mockUser.sub);
        return;
      }
      
      // For dev and prod environments, use Okta authentication
      console.log('🔐 Non-local environment - using Okta authentication');
      await oktaAuth.signInWithRedirect({
        scopes: ['openid', 'profile', 'email', 'groups']
      });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Clear local state first
      setIsAuthenticated(false);
      setUser(null);
      setUserGroups([]);
      setMatchedGroups([]);
      setIsAuthorized(false);
      setAuthorizationError(null);
      
      // Check if we're in local environment
      if (isLocalEnvironment()) {
        console.log('🏠 Local environment logout - clearing local session');
        // Clear local auth data
        sessionStorage.removeItem('localAuthBypass');
        sessionStorage.removeItem('localUserData');
        console.log('✅ Local logout successful');
        return;
      }
      
      // For dev and prod environments, use Okta logout
      console.log('🔐 Non-local environment - using Okta logout');
      await oktaAuth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {
      // For local environment, return a mock token
      if (isLocalEnvironment()) {
        const localAuthBypass = sessionStorage.getItem('localAuthBypass');
        if (localAuthBypass === 'true') {
          return 'mock-local-access-token';
        }
        return null;
      }
      
      // For non-local environments, use Okta token management
      const tokenManager = oktaAuth.tokenManager;
      const accessToken = await tokenManager.get('accessToken');
      
      if (accessToken && !tokenManager.hasExpired(accessToken)) {
        return (accessToken as any).value || (accessToken as any).accessToken;
      }
      
      // Token expired, try to refresh
      await refreshTokens();
      const newAccessToken = await tokenManager.get('accessToken');
      return (newAccessToken as any)?.value || (newAccessToken as any)?.accessToken || null;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  };

  const refreshTokens = async (): Promise<void> => {
    try {
      // For local environment, refreshTokens is a no-op since we use mock authentication
      if (isLocalEnvironment()) {
        console.log('🏠 Local environment - token refresh not needed');
        return;
      }
      
      // For non-local environments, use Okta token refresh
      const tokenManager = oktaAuth.tokenManager;
      await tokenManager.renew('accessToken');
      await tokenManager.renew('idToken');
    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, logout user
      await logout();
      throw error;
    }
  };

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    userGroups,
    matchedGroups,
    isAuthorized,
    authorizationError,
    login,
    logout,
    getAccessToken,
    refreshTokens,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;